import * as THREE from 'three';
import { memo, useCallback, useMemo, useState } from 'react';
import { type ThreeEvent } from '@react-three/fiber';
import type { CuboidProps } from '../types';
import { CuboidVariants } from './Enums';
import { ViewerLayer } from './ViewerLayer';
import { useOnClick } from './useOnClick';
import { useAbyssViewerContext } from './AbyssViewerContext';
import { RENDER_ORDER_CUBOID, RENDER_ORDER_TRANSFORM_CONTROLS } from './RenderOrder';

export const Cuboid = memo((cuboid: CuboidProps) => {
  const {
    isVisible,
    position,
    scale,
    rotation,
    style,
    hoveredStyle,
    onClick,
    onHover,
    showInSphericalView,
    enableClippingByVisibilityBox,
    enableSilhouette = false,
  } = cuboid;

  const { sharedGeometries, clippingPlanes } = useAbyssViewerContext();
  const { boxGeometry, cuboidLines } = sharedGeometries ?? {};

  const meshLayers = useMemo(() => {
    const layers = new THREE.Layers();
    if (isVisible ?? true) {
      layers.enable(ViewerLayer.Default);
      if (onClick) {
        layers.enable(ViewerLayer.AutoPickable);
      }
      if (showInSphericalView) {
        layers.enable(ViewerLayer.SphericalViewerLayer);
      }
    } else {
      layers.disable(ViewerLayer.Default);
      layers.disable(ViewerLayer.AutoPickable);
      layers.disable(ViewerLayer.SphericalViewerLayer);
    }
    return layers;
  }, [isVisible, showInSphericalView, onClick]);

  const linesLayers = useMemo(() => {
    const layers = new THREE.Layers();
    if (isVisible ?? true) {
      layers.enable(ViewerLayer.Default);
    } else {
      layers.disable(ViewerLayer.Default);
    }
    return layers;
  }, [isVisible]);

  const shouldHandle = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey, button } = event;
    const isLeftButton = button === 0;
    if (!isLeftButton || altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const onMeshClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (onClick) {
        event.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const [isHovered, setIsHovered] = useState<boolean>(false);

  const handleHovered = useCallback(
    (newIsHovered: boolean) => {
      setIsHovered(newIsHovered);
      onHover?.(newIsHovered);
    },
    [onHover]
  );

  const onPointerOver = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // console.log('onPointerOver', event);
      handleHovered(true);
      event.stopPropagation();
    },
    [handleHovered]
  );
  const onPointerOut = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      // console.log('onPointerOut', event);
      handleHovered(false);
      event.stopPropagation();
    },
    [handleHovered]
  );

  const { onPointerDown, onPointerUp } = useOnClick({ onClick: onMeshClick, shouldHandle });

  const activeStyle = isHovered ? (hoveredStyle ?? style) : style;
  const { variant, lineColor, lineOpacity, lineWidth, fillColor, fillOpacity } = activeStyle;

  const optionalClippingPlanes = (enableClippingByVisibilityBox ?? true) ? clippingPlanes : null;

  return (
    <group scale={scale} position={position} rotation={rotation}>
      <lineSegments
        geometry={cuboidLines}
        layers={linesLayers}
        renderOrder={enableSilhouette ? RENDER_ORDER_TRANSFORM_CONTROLS : RENDER_ORDER_CUBOID}
      >
        <lineBasicMaterial
          attach="material"
          clippingPlanes={optionalClippingPlanes}
          color={lineColor}
          transparent
          opacity={lineOpacity}
          linewidth={lineWidth}
          depthWrite={false}
          depthTest={!enableSilhouette}
        />
      </lineSegments>
      {variant === CuboidVariants.LinesOnly ? null : (
        <mesh
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          geometry={boxGeometry}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
          layers={meshLayers}
          renderOrder={RENDER_ORDER_CUBOID}
        >
          <meshBasicMaterial
            attach="material"
            clippingPlanes={optionalClippingPlanes}
            color={fillColor}
            wireframe={variant === CuboidVariants.LinesAndWireframeMesh}
            transparent={variant === CuboidVariants.LinesAndTransparentMesh}
            opacity={fillOpacity}
            forceSinglePass={true}
            depthWrite={false}
          />
        </mesh>
      )}
    </group>
  );
});
